import type { DashboardTab } from '@/types'
import { getMonthOptions } from '@/utils/dateParser'

interface HeaderProps {
  selectedMonth: number
  selectedYear: number
  activeTab: DashboardTab
  loading: boolean
  onMonthChange: (month: number, year: number) => void
  onTabChange: (tab: DashboardTab) => void
  onRefresh: () => void
  lastUpdated: Date | null
}

export function Header({
  selectedMonth,
  selectedYear,
  activeTab,
  loading,
  onMonthChange,
  onTabChange,
  onRefresh,
  lastUpdated,
}: HeaderProps) {
  const monthOptions = getMonthOptions()

  const selectedValue = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`

  function handleMonthChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const [year, month] = e.target.value.split('-').map(Number)
    onMonthChange(month, year)
  }

  return (
    <header className="bg-brand-black text-white sticky top-0 z-10 shadow-lg">
      <div className="max-w-screen-xl mx-auto px-6 py-4">
        {/* Top row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold tracking-tight leading-none">
              Dashboard de Produção
            </h1>
            <p className="text-brand-taupe text-xs mt-0.5 font-light tracking-widest uppercase">
              Agência LLUMI
            </p>
          </div>

          {lastUpdated && (
            <p className="text-brand-taupe text-xs hidden sm:block">
              Atualizado às{' '}
              {lastUpdated.toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {/* Month selector */}
          <div className="flex items-center gap-2">
            <label htmlFor="month-select" className="text-xs text-brand-taupe whitespace-nowrap">
              Mês
            </label>
            <select
              id="month-select"
              value={selectedValue}
              onChange={handleMonthChange}
              className="bg-white/10 border border-white/20 text-white text-sm rounded-xl
                         px-3 py-1.5 appearance-none cursor-pointer
                         focus:outline-none focus:ring-2 focus:ring-brand-taupe
                         hover:bg-white/15 transition-colors capitalize"
            >
              {monthOptions.map((opt) => (
                <option
                  key={`${opt.year}-${opt.month}`}
                  value={`${opt.year}-${String(opt.month).padStart(2, '0')}`}
                  className="bg-brand-black text-white capitalize"
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tab selector */}
          <div className="flex rounded-xl overflow-hidden border border-white/20">
            <TabButton active={activeTab === 'production'} onClick={() => onTabChange('production')}>
              Produção
            </TabButton>
            <TabButton active={activeTab === 'scheduling'} onClick={() => onTabChange('scheduling')}>
              Agendamento
            </TabButton>
            <TabButton active={activeTab === 'costs'} onClick={() => onTabChange('costs')}>
              Custos
            </TabButton>
          </div>

          {/* Refresh button */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="ml-auto flex items-center gap-2 border border-white/20 text-white
                       px-4 py-1.5 rounded-xl text-sm font-medium
                       hover:bg-white/10 transition-colors disabled:opacity-50
                       disabled:cursor-not-allowed"
          >
            <RefreshIcon spinning={loading} />
            {loading ? 'Atualizando...' : 'Atualizar dados'}
          </button>
        </div>
      </div>
    </header>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 sm:px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
        active ? 'bg-white text-brand-black' : 'text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  )
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={spinning ? 'animate-spin' : ''}
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  )
}
